import React, { useEffect } from 'react';
import 'card-pattern/dist/main.css';
import { getAdminCardGroups, LandingCard } from '../../components/common/config/landingCards';
import { Link } from 'react-router-dom';
import ApproverResources from '../Approver/ListPages/ApproverResources';
import { useAppDispatch, useAppSelector } from '../../store/hooks';
import { fetchProfile } from '../../store/slices/profileSlice';
import Loader from '../../components/common/Loader';
import ValidationBanner from '../../components/common/ValidationBanner';

/** Renders a single admin landing card as a `fieldset`. */
const AdminCard: React.FC<{ card: LandingCard }> = ({ card }) => {
  const titleId = `card-${card.title.replace(/\s+/g, '-').toLowerCase()}-title`
  return (
    <fieldset
      className="ds-card"
      data-ds-theme="vessel"
      data-ds-variant="thumbnail"
      data-ds-version="2"
      aria-labelledby={titleId}
    >
      <div className="ds-card__image shrink-0! w-11! h-11!">
        <img src={card.icon} alt="card icons" width="44" height="44" className="shrink-0!" />
      </div>
      <div className="ds-card__content">
        <div className="p3 -medium mb-2! text-[16px]!" id={titleId}>
          {card.title}
        </div>
        {card.description && (
          <div className="text-[14px] mb-2">{card.description}</div>
        )}
        <div className="ds-card__link-container mt-4">
          <div className="ds-card__text-link">
            {card.link.startsWith('http') ? (
              <a href={card.link} target={card.target} rel="noopener noreferrer" className="inline-link medium no-underline!">
                {card.linkText ?? 'Open'}
              </a>
            ) : (
              <Link to={card.link} className="inline-link medium no-underline!">
                {card.linkText ?? 'Open'}
              </Link>
            )}
          </div>
        </div>
      </div>
    </fieldset>
  )
}

/** Refreshes the profile on entry before displaying role-based admin cards. */
const LandingPage: React.FC = () => {
  const dispatch = useAppDispatch()
  const roles = useAppSelector((state) => state.profile?.data?.roles)
  const loading = useAppSelector((state) => state.profile.loading)
  const error = useAppSelector((state) => state.profile.error)
  const { assignmentCards, commonCards } = getAdminCardGroups(roles ?? {})

  useEffect(() => {
    void dispatch(fetchProfile(true))
  }, [dispatch])

  if (loading) return <Loader />

  if (error) {
    return (
      <div className="page-container">
        <ValidationBanner variant="error" message={error} compact={false} />
      </div>
    )
  }

  return (
    <div className="page-container" style={{ minHeight: '460px' }}>
      <div className="margin-bottom-2u margin-top-5u">
        <h1 className="h1 mb-8!">TORT Administration</h1>
      </div>

 

      <div className="flex flex-col gap-y-8 margin-top-3u">
        {/* Flag-driven assignment cards: count/order varies with roles, grid reflows with no gaps. */}
        {assignmentCards.length > 0 && (
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-x-4! gap-y-8">
            {assignmentCards.map((card) => (
              <AdminCard key={card.title} card={card} />
            ))}
          </div>
        )}
        {/* Common cards always form their own row, after any assignment cards. */}
        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-x-4! gap-y-8">
          {commonCards.map((card) => (
            <AdminCard key={card.title} card={card} />
          ))}
        </div>
      </div>
      <ApproverResources/>
    </div>
  );
};

export default LandingPage;
